import { control, curve, display, readout, type PhysicsDomain } from './domainTypes';

export const electromagnetism: PhysicsDomain = {
  id: 'electromagnetism',
  name: 'Electromagnetism Lab',
  hint: 'Test how current, field direction and changing flux affect forces and induced voltage.',
  experiments: [
    {
      id: 'motor',
      name: 'Motor effect',
      question: 'Which way does the wire move when you reverse the current?',
      controls: [
        control('b', 'Magnetic field', 'T', -1, 1, 0.5, 0.05),
        control('i', 'Current', 'A', -10, 10, 2, 0.5),
        control('l', 'Wire length', 'm', 0.05, 1, 0.2, 0.05),
        control('angle', 'Angle between wire and field', '°', 0, 180, 90, 5),
      ],
      examples: [
        {
          id: 'perpendicular',
          name: 'Across the field',
          values: {},
          note: 'A perpendicular wire experiences the largest force for fixed B, I and L.',
        },
        {
          id: 'reverse',
          name: 'Reverse the current',
          values: { i: -2 },
          note: 'Changing current direction reverses force direction.',
        },
        {
          id: 'parallel',
          name: 'Along the field',
          values: { angle: 0 },
          note: 'Parallel current and field produce no magnetic force.',
        },
      ],
      assumptions:
        'Straight wire in a uniform field. Positive current points right; positive perpendicular field points up. Positive force points out of the page. Angle rotates the field from right towards up.',
      source:
        'https://openstax.org/books/university-physics-volume-2/pages/11-4-magnetic-force-on-a-current-carrying-conductor',
      calculate: ({ b, i, l, angle }) => {
        const force = b * i * l * Math.sin((angle * Math.PI) / 180);
        return {
          readouts: [readout('Signed force', force, 'N')],
          summary:
            Math.abs(force) < 1e-12
              ? 'There is no magnetic force on this wire.'
              : `The force points ${force > 0 ? 'out of' : 'into'} the page.`,
          working: [
            'F = B I L sin θ',
            `F = ${b} × ${i} × ${l} × sin ${angle}° = ${display(force)} N`,
          ],
          plot: curve(180, (a) => b * i * l * Math.sin((a * Math.PI) / 180)),
          xLabel: 'Angle (°)',
          yLabel: 'Signed force (N)',
        };
      },
      prediction: {
        question: 'Reverse current while keeping the field fixed. What changes?',
        options: ['The force reverses', 'The force doubles', 'The force stays the same'],
        answer: 0,
        why: 'Force follows the cross product of current direction and magnetic field.',
      },
      task: 'Use B = 0.4 T, I = 3 A and L = 0.5 m. Predict the force at 30° and 90°, then test both.',
    },
    {
      id: 'induction',
      name: 'Changing flux',
      question: 'Can a strong, steady magnetic field induce a voltage in a stationary coil?',
      controls: [
        control('turns', 'Coil turns', '', 1, 200, 50),
        control('area', 'Coil area', 'm²', 0.01, 1, 0.1, 0.01),
        control('start', 'Initial field', 'T', -1, 1, 0, 0.05),
        control('end', 'Final field', 'T', -1, 1, 0.5, 0.05),
        control('duration', 'Change duration', 's', 0.1, 10, 1, 0.1),
      ],
      examples: [
        {
          id: 'rising',
          name: 'Increasing field',
          values: {},
          note: 'Changing flux creates an induced voltage.',
        },
        {
          id: 'steady',
          name: 'Strong but steady',
          values: { start: 1, end: 1 },
          note: 'A stationary coil with steady flux has no induced voltage.',
        },
        {
          id: 'falling',
          name: 'Decreasing field',
          values: { start: 0.5, end: 0 },
          note: 'Reversing the flux change reverses induced voltage.',
        },
      ],
      assumptions:
        'Uniform field normal to a stationary coil, changing linearly over the chosen interval. This shows interval-average emf, not an AC generator. Positive loop direction follows the right-hand rule for the positive field normal.',
      source: 'https://openstax.org/books/university-physics-volume-2/pages/13-1-faradays-law',
      calculate: ({ turns, area, start, end, duration }) => {
        const flux = area * (end - start);
        const emf = (-turns * flux) / duration;
        return {
          readouts: [
            readout('Flux change per turn', flux, 'Wb'),
            readout('Induced voltage', emf, 'V'),
          ],
          summary:
            emf === 0
              ? 'Steady flux induces no voltage, even in a strong field.'
              : 'The induced voltage opposes the change in magnetic flux.',
          working: [
            'ΔΦ = A(Bfinal − Binitial)',
            `ΔΦ = ${area} × (${end} − ${start}) = ${display(flux)} Wb`,
            'ε = −N ΔΦ / Δt',
            `ε = −${turns} × ${display(flux)} / ${duration} = ${display(emf)} V`,
          ],
          plot: curve(duration, (t) => area * (start + ((end - start) * t) / duration)),
          xLabel: 'Time (s)',
          yLabel: 'Flux per turn (Wb)',
        };
      },
      prediction: {
        question:
          'A stationary coil sits in a strong, unchanging field. What is the induced voltage?',
        options: ['Zero', 'A large voltage', 'It grows each second'],
        answer: 0,
        why: 'Induction depends on changing flux, rather than field strength alone.',
      },
      task: 'Predict what happens to voltage if the same flux change takes half as long. Test it and explain the minus sign.',
    },
    {
      id: 'motional',
      name: 'Moving conductor',
      question: 'How does moving a wire through a field produce voltage?',
      controls: [
        control('b', 'Magnetic field', 'T', -1, 1, 0.5, 0.05),
        control('l', 'Rod length', 'm', 0.05, 1, 0.2, 0.05),
        control('v', 'Rod velocity', 'm/s', -10, 10, 3, 0.5),
      ],
      examples: [
        {
          id: 'moving',
          name: 'Move the rod',
          values: {},
          note: 'Charges separate along the moving conductor.',
        },
        {
          id: 'stopped',
          name: 'Stop the rod',
          values: { v: 0 },
          note: 'No motion means no motional emf in this setup.',
        },
        {
          id: 'reverse',
          name: 'Reverse motion',
          values: { v: -3 },
          note: 'Reversing motion reverses the voltage polarity.',
        },
      ],
      assumptions:
        'Rod, velocity and uniform field are mutually perpendicular. Positive velocity points right; positive field points into the page; positive voltage means the top end is at higher potential. Open circuit, no current or load.',
      source: 'https://openstax.org/books/university-physics-volume-2/pages/13-3-motional-emf',
      calculate: ({ b, l, v }) => {
        const emf = b * l * v;
        return {
          readouts: [readout('Top minus bottom voltage', emf, 'V')],
          summary:
            emf === 0
              ? 'No charge-separating voltage appears in this setup.'
              : `The top end is at ${emf > 0 ? 'higher' : 'lower'} potential than the bottom.`,
          working: ['ε = B L v', `ε = ${b} × ${l} × ${v} = ${display(emf)} V`],
          plot: curve(10, (speed) => b * l * speed),
          xLabel: 'Positive velocity (m/s)',
          yLabel: 'Voltage (V)',
        };
      },
      prediction: {
        question: 'Double the rod speed. What happens to voltage?',
        options: ['It doubles', 'It stays constant', 'It halves'],
        answer: 0,
        why: 'For a fixed perpendicular field and rod length, ε = B L v.',
      },
      task: 'Find a rod velocity that produces 0.6 V with B = 0.5 T and L = 0.2 m. Test your calculation.',
    },
  ],
};
